import type {
  FactoryOptionDto,
  MachineAttachmentDto,
  MachineAttachmentItemDto,
  MachineAttachmentItemPriceOnlyDto,
  MachineDto,
  MachineItemPriceOnlyDto,
  MachineQuotationItemDto
} from '@api/machines'
import {
  ARCHIVED_ITEMS,
  EqOptionType,
  formatPriceAndCurrency,
  matchOldPriceItem,
  Options,
  OTHER
} from '@common/utils'
import {
  MolsonArchived,
  MolsonBox,
  MolsonCheckbox,
  MolsonIcon,
  MolsonRadioButton,
  MolsonRadioGroup,
  MolsonSpinner,
  MolsonTypography
} from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ArchivedNotification } from '../archived-notification/ArchivedNotification'
import { FACTORY_OPTIONS_TYPES } from '../constants'
import type {
  FactoryOptionDtoWithChecked,
  HandleOptionChangeFunctionProps
} from '../types'

export type FactoryOptionProps = {
  archivedFactoryFits: FactoryOptionDtoWithChecked[]
  factoryFits: FactoryOptionDto[]
  setTypeName: string
  isFamily: boolean
  isGroup: boolean
  handleAddFactoryOption: (
    event: ChangeEvent<HTMLInputElement>,
    factoryOption: FactoryOptionDto
  ) => void
  handleOptionChange: HandleOptionChangeFunctionProps
  machine?: MachineAttachmentDto | MachineDto
  quotationItem?: MachineAttachmentItemDto | MachineQuotationItemDto
  quotationItemOldPrices: MachineAttachmentItemPriceOnlyDto &
    MachineItemPriceOnlyDto
  setArchivedFactoryFits: (values: FactoryOptionDtoWithChecked[]) => void
  setFactoryFitsNames?: (values: string[]) => void
}

export const FactoryOption = ({
  archivedFactoryFits,
  factoryFits,
  setTypeName,
  isFamily,
  isGroup,
  handleAddFactoryOption,
  handleOptionChange,
  machine,
  quotationItem,
  quotationItemOldPrices,
  setArchivedFactoryFits,
  setFactoryFitsNames
}: FactoryOptionProps) => {
  const {
    attachmentId,
    equipmentId,
    quoteId: quoteIdParam,
    specId
  } = useParams()

  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })
  const theme = molsonUseTheme()
  const quoteId = quoteIdParam ?? specId

  const itemKey = `${ARCHIVED_ITEMS.FACTORY_FIT}_${setTypeName}`

  const [loadingOptionId, setLoadingOptionId] = useState<number | null>(null)

  // Track previous factoryOptionItems to clear loading when mutation completes
  const prevFactoryOptionsRef = useRef(quotationItem?.factoryOptionItems)
  useEffect(() => {
    if (prevFactoryOptionsRef.current !== quotationItem?.factoryOptionItems) {
      prevFactoryOptionsRef.current = quotationItem?.factoryOptionItems
      setLoadingOptionId(null)
    }
  }, [quotationItem?.factoryOptionItems])

  const [selectedFactoryFits, setSelectedFactoryFits] = useState<
    FactoryOptionDto[]
  >([])

  const getDisplayedOptions = useMemo(() => {
    return factoryFits?.filter(
      item =>
        !item.isArchived ||
        archivedFactoryFits?.some(option => option.id === item.id)
    )
  }, [factoryFits, archivedFactoryFits])

  const optionForFamily = selectedFactoryFits?.find(
    item => item.family === setTypeName || item.group === setTypeName
  )

  const uncheckArchivedItems = () => {
    if (setArchivedFactoryFits) {
      const updatedValues = archivedFactoryFits?.map(item => {
        if (item.family === setTypeName || item.group === setTypeName) {
          return {
            ...item,
            checked: false
          }
        }
        return item
      })

      setArchivedFactoryFits(updatedValues)
    }
  }

  const handleOptionGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const optionId = Number(e.target.value)
    setLoadingOptionId(optionId)

    if (equipmentId && attachmentId) {
      handleOptionChange({
        machineId: equipmentId,
        option: Options.ADD_FACTORY_FIT_WITHIN_MACHINE,
        optionId,
        quotationId: String(quoteId)
      })
      uncheckArchivedItems()
      return
    }

    handleOptionChange({
      option: Options.ADD_FACTORY_FIT,
      optionId,
      quotationId: String(quoteId)
    })

    uncheckArchivedItems()
  }

  const getFactoryOptionLabel = (
    item: FactoryOptionDto,
    showOnlyPrice = false
  ) => {
    const { dnp = 0, id = 0 } = item
    const oldPrice = matchOldPriceItem(quotationItemOldPrices, {
      id: id,
      price: dnp ?? 0,
      type: 'factoryOptionItems'
    })

    return (
      <MolsonBox display="flex" flexDirection="column">
        {!showOnlyPrice && (
          <MolsonTypography
            sx={{
              color: theme.palette.primary.main
            }}
            variant="h4"
          >
            {item.name}
          </MolsonTypography>
        )}
        <MolsonBox alignItems="center" display="flex" gap={0.5}>
          {oldPrice !== undefined && (
            <>
              <MolsonTypography
                sx={{
                  color: theme.palette.secondary[500],
                  textDecoration: 'line-through'
                }}
                variant="body2"
              >
                {formatPriceAndCurrency(oldPrice)}
              </MolsonTypography>
              <MolsonIcon
                variant={
                  oldPrice < (dnp ?? 0) ? 'ARROW_UPWARD' : 'ARROW_DOWNWARD'
                }
                iconColor={oldPrice < (dnp ?? 0) ? 'success' : 'error'}
              />
            </>
          )}

          <MolsonTypography
            sx={{
              color: theme.palette.secondary[600],
              fontWeight: 'bold'
            }}
            variant="body2"
          >
            {formatPriceAndCurrency(dnp)}
          </MolsonTypography>
        </MolsonBox>
      </MolsonBox>
    )
  }

  const renderArchivedOtherSetType = (item: FactoryOptionDto) => {
    return (
      <MolsonBox
        data-search={item.name}
        data-family={item.family || item.group}
        data-eq-type={EqOptionType.FACTORY_FIT}
        key={item.id}
        display="flex"
        flexDirection="column"
      >
        <MolsonArchived
          checked={
            item.type === FACTORY_OPTIONS_TYPES.FACTORY_FIT
              ? archivedFactoryFits?.find(
                  archivedFit => archivedFit.id === item.id
                )?.checked
              : false
          }
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            handleAddFactoryOption(event, item)
          }
          id={itemKey}
          label={`${item.name}`}
          outerBoxSx={{ height: '100%', width: '100%' }}
          secondLabel={undefined}
        />

        <ArchivedNotification text={t('unavailableItem')} />
      </MolsonBox>
    )
  }

  const renderUnarchivedOtherSetType = (item: FactoryOptionDto) => {
    return (
      <MolsonBox
        data-search={item.name}
        data-eq-type={EqOptionType.FACTORY_FIT}
        data-family={item.family || item.group}
        key={item.id}
      >
        <MolsonCheckbox
          checked={
            machine &&
            quotationItem &&
            item.type === FACTORY_OPTIONS_TYPES.FACTORY_FIT
              ? !!quotationItem.factoryOptionItems?.find(
                  (quotationItem: FactoryOptionDto) =>
                    quotationItem.id === item.id
                )
              : false
          }
          key={item.id}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            handleAddFactoryOption(event, item)
          }
          label={item.name ?? undefined}
          secondLabel={getFactoryOptionLabel(item, true)}
        />
      </MolsonBox>
    )
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const updatedSelectedFactoryFits = (
      quotationItem?.factoryOptionItems ?? []
    ).map(element1 => {
      const match = getDisplayedOptions.find(
        element2 => element2.id === element1.id
      )
      return match ? { ...element1, ...match } : element1
    })

    setSelectedFactoryFits(updatedSelectedFactoryFits)
  }, [quotationItem?.factoryOptionItems])

  // ignore lint to avoid re-render loop
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setFactoryFitsNames?.(
      getDisplayedOptions.map(item => item.name) as string[]
    )
  }, [])

  if (setTypeName === OTHER) {
    return (
      <MolsonBox
        display="grid"
        gap={2}
        gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))"
        width="100%"
      >
        {isFamily &&
          getDisplayedOptions.map(item => {
            if (item.isArchived && item.family === OTHER) {
              return renderArchivedOtherSetType(item)
            }

            if (!item.isArchived && item.family === OTHER) {
              return renderUnarchivedOtherSetType(item)
            }
          })}

        {isGroup &&
          getDisplayedOptions.map(item => {
            if (item.isArchived && item.group === OTHER) {
              return renderArchivedOtherSetType(item)
            }

            if (!item.isArchived && item.group === OTHER) {
              return renderUnarchivedOtherSetType(item)
            }
          })}
      </MolsonBox>
    )
  }

  return (
    <MolsonRadioGroup
      sx={{
        width: '100%'
      }}
      onChange={handleOptionGroupChange}
      row={true}
      value={optionForFamily?.id ?? ''}
    >
      <MolsonBox
        display="grid"
        gap={2}
        gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))"
        width="100%"
      >
        {getDisplayedOptions?.map(item => {
          if (isFamily && item.isArchived && item.family === setTypeName) {
            return (
              <MolsonBox
                id={itemKey}
                key={item.id}
                data-search={item.name}
                data-eq-type={EqOptionType.FACTORY_FIT}
                data-family={item.family || item.group}
              >
                <MolsonBox>
                  <MolsonArchived
                    outerBoxSx={{
                      pb: 0,
                      pl: 0,
                      pt: 0
                    }}
                    radioChecked={
                      item.type === FACTORY_OPTIONS_TYPES.FACTORY_FIT
                        ? archivedFactoryFits?.find(
                            archivedFit => archivedFit.id === item.id
                          )?.checked
                        : false
                    }
                    checked={true}
                    disabled={true}
                    hasRadioButton={true}
                    id={itemKey}
                    label={`${item.name}`}
                    secondLabel={undefined}
                  />

                  <ArchivedNotification text={t('unavailableItem')} />
                </MolsonBox>
              </MolsonBox>
            )
          }

          if (isFamily && !item.isArchived && item.family === setTypeName) {
            const isSelected = selectedFactoryFits.some(
              element => element.id === item.id
            )
            const isItemLoading = loadingOptionId === item.id

            return (
              <MolsonBox
                data-search={item.name}
                data-eq-type={EqOptionType.FACTORY_FIT}
                data-family={item.family ?? item.group}
                key={item.id}
                sx={{ position: 'relative' }}
              >
                {isItemLoading && (
                  <MolsonBox
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      zIndex: 1
                    }}
                  >
                    <MolsonSpinner size={20} />
                  </MolsonBox>
                )}
                <MolsonRadioButton
                  key={item.id}
                  checked={isSelected}
                  formControlLabelProps={{
                    label: getFactoryOptionLabel(item)
                  }}
                  radioBtnProps={{
                    value: item.id,
                    disabled: loadingOptionId !== null
                  }}
                />
              </MolsonBox>
            )
          }

          if (isGroup && item.isArchived && item.group === setTypeName) {
            return (
              <MolsonBox
                data-search={item.name}
                data-eq-type={EqOptionType.FACTORY_FIT}
                data-family={item.family || item.group}
                key={item.id}
              >
                <MolsonBox key={item.id}>
                  <MolsonArchived
                    checked={
                      item.type === FACTORY_OPTIONS_TYPES.FACTORY_FIT
                        ? archivedFactoryFits?.find(
                            archivedFit => archivedFit.id === item.id
                          )?.checked
                        : false
                    }
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleAddFactoryOption(event, item)
                    }
                    id={itemKey}
                    label={`${item.name}`}
                    outerBoxSx={{ height: '100%', width: '100%' }}
                    secondLabel={undefined}
                  />

                  <ArchivedNotification text={t('unavailableItem')} />
                </MolsonBox>
              </MolsonBox>
            )
          }

          if (isGroup && !item.isArchived && item.group === setTypeName) {
            const isItemLoading = loadingOptionId === item.id
            return (
              <MolsonBox
                data-search={item.name}
                data-eq-type={EqOptionType.FACTORY_FIT}
                data-family={item.family || item.group}
                key={item.id}
                sx={{ position: 'relative' }}
              >
                {isItemLoading && (
                  <MolsonBox
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      zIndex: 1
                    }}
                  >
                    <MolsonSpinner size={20} />
                  </MolsonBox>
                )}
                <MolsonCheckbox
                  checked={
                    machine &&
                    quotationItem &&
                    item.type === FACTORY_OPTIONS_TYPES.FACTORY_FIT
                      ? !!quotationItem.factoryOptionItems?.find(
                          (quotationItem: FactoryOptionDto) =>
                            quotationItem.id === item.id
                        )
                      : false
                  }
                  disabled={isItemLoading}
                  key={item.id}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    handleAddFactoryOption(event, item)
                  }
                  label={item.name ?? undefined}
                  secondLabel={getFactoryOptionLabel(item, true)}
                />
              </MolsonBox>
            )
          }
        })}
      </MolsonBox>
    </MolsonRadioGroup>
  )
}
